<template>
  <div class="total-calculations">
    <!-- Base price calculation -->
    <p class="flex flex-nowrap pd-t-small">
      <span class="w-100 opacity-50">{{ t('subtotal') }}</span>
      <span class="w-100 t-right">{{ formatPrice({ amount: totalPrice, currency }) }}</span>
    </p>

    <!-- Platform fee (conditional) -->
    <p v-if="showFees" class="flex flex-nowrap pd-t-small">
      <span class="w-100 opacity-50">{{ t('feesRate') }}</span>
      <span class="w-100 t-right">{{ formatPrice({ amount: feesAmount, currency }) }}</span>
    </p>

    <!-- VAT calculation (conditional) -->
    <p v-if="showVat" class="flex flex-nowrap pd-t-small">
      <span class="w-100 opacity-50">{{ t('vat', { rate: vatRate * 100 }) }}</span>
      <span class="w-100 t-right">{{ formatPrice({ amount: vatAmount, currency }) }}</span>
    </p>

    <!-- Delivery fee (conditional) -->
    <p v-if="showDeliveryFee" class="flex flex-nowrap br-subtle br-b-1px pd-b-small pd-t-small">
      <span class="w-100 opacity-50">{{ t('delivery') }}</span>
      <span class="w-100 t-right">{{ formatPrice({ amount: deliveryAmount, currency }) }}</span>
    </p>

    <!-- Grand total -->
    <p class="p-medium flex flex-nowrap pd-b-small pd-t-small">
      <span class="w-100 fw-medium">{{ t('intotal') }}</span>
      <span class="w-100 t-accent fw-medium t-right">{{ formatPrice({ amount: grandTotal, currency }) }}</span>
    </p>
  </div>
</template>


<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js';

const { formatPrice } = useGlobalMixins();

const props = defineProps({
  totalPrice: {
    type: Number,
    required: true
  },
  currency: {
    type: String,
    required: true
  },
  showFees: {
    type: Boolean,
    default: false
  },
  feesRate: {
    type: Number,
    default: 0
  },
  showVat: {
    type: Boolean,
    default: false
  },
  vatRate: {
    type: Number,
    default: 0
  },
  showDeliveryFee: {
    type: Boolean,
    default: false
  },
  deliveryRate: {
    type: Number,
    default: 0
  }
});

const text = {
  messages: {
    en: {
      subtotal: 'Subtotal',
      feesRate: 'Platform Fee',
      vat: 'VAT ({rate}%)',
      delivery: 'Delivery',
      intotal: 'In Total'
    },
    ru: {
      subtotal: 'Подытог',
      feesRate: 'Плата за платформу',
      vat: 'НДС ({rate}%)',
      delivery: 'Доставка',
      intotal: 'Итого'
    }
  }
};

const { t } = useI18n(text);

// Все суммы — целые в минорных единицах валюты заказа. Ставки (НДС, комиссия)
// это доли, поэтому результат округляем: дробных копеек не бывает.
const vatAmount = computed(() => {
  if (!props.showVat) return 0;
  return Math.round(props.totalPrice * props.vatRate);
});

const feesAmount = computed(() => {
  if (!props.showFees) return 0;
  return Math.round(props.totalPrice * props.feesRate);
});

const deliveryAmount = computed(() => {
  if (!props.showDeliveryFee) return 0;
  return Math.round(props.deliveryRate);
});

const grandTotal = computed(() => {
  return props.totalPrice + feesAmount.value + vatAmount.value + deliveryAmount.value;
});
</script>
